import { useState } from 'react';
import {
  Button, Stack, Typography, Alert, Divider, Box, CircularProgress, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, Tooltip,
} from '@mui/material';
import { Search, PriceCheck } from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { FileUploadField } from '../../components/FileUploadField';
import { rateLookupApi, type LookupBoqRow, type LookupResultRow } from '../../api/boqMasters.api';
import { extractApiError } from '../../api/error';

const inr = (n?: number | null) => (n == null ? '—' : `₹${Number(n).toLocaleString('en-IN')}`);

export function RateLookupPage() {
  const [fileUrl, setFileUrl] = useState('');
  const [parsed, setParsed] = useState<LookupBoqRow[]>([]);
  const [results, setResults] = useState<LookupResultRow[] | null>(null);
  const [summary, setSummary] = useState<{ itemCount: number; matchedCount: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const parseMut = useMutation({
    mutationFn: () => rateLookupApi.parse(fileUrl),
    onSuccess: (r) => {
      setError(null); setResults(null); setSummary(null);
      if (!r.rows.length) { setInfo(r.message ?? 'No line items detected — check the file.'); setParsed([]); return; }
      setParsed(r.rows);
      setInfo(`Parsed ${r.rows.length} line item(s). Click "Find Rates" to pick rates from client rate cards & purchases.`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const lookupMut = useMutation({
    mutationFn: () => rateLookupApi.lookup(parsed),
    onSuccess: (r) => { setResults(r.rows); setSummary({ itemCount: r.itemCount, matchedCount: r.matchedCount }); setInfo(null); setError(null); },
    onError: (e) => setError(extractApiError(e)),
  });

  return (
    <>
      <PageHeader title="BOQ Rate Lookup" subtitle="Upload a BOQ with no rates → the system finds each line's rate across ALL client rate cards (with source) and shows your most recent purchase price & warehouse." />
      <Stack spacing={2} sx={{ maxWidth: 1200 }}>
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

        <Box sx={{ bgcolor: 'action.hover', borderRadius: 1.5, p: 1.5 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
            <Box sx={{ flex: 1, minWidth: 260 }}>
              <FileUploadField label="BOQ spreadsheet (.xlsx/.xls/.csv)" accept=".xlsx,.xls,.csv" value={fileUrl} onChange={(url) => setFileUrl(url ?? '')} />
            </Box>
            <Button startIcon={parseMut.isPending ? <CircularProgress size={16} /> : <Search />} variant="outlined" disabled={!fileUrl || parseMut.isPending} onClick={() => { setInfo(null); parseMut.mutate(); }}>
              Parse file
            </Button>
            <Button startIcon={lookupMut.isPending ? <CircularProgress size={16} /> : <PriceCheck />} variant="contained" disabled={!parsed.length || lookupMut.isPending} onClick={() => lookupMut.mutate()}>
              Find Rates
            </Button>
          </Stack>
          {info && <Alert severity="info" sx={{ mt: 1, py: 0 }}>{info}</Alert>}
          {summary && <Alert severity="success" sx={{ mt: 1, py: 0 }}>{summary.matchedCount} of {summary.itemCount} item(s) matched a rate.</Alert>}
        </Box>

        {results && (
          <>
            <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Rate lookup results</Typography></Divider>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Description</TableCell><TableCell>Unit</TableCell><TableCell align="right">Qty</TableCell>
                <TableCell align="right">Best Client Rate</TableCell><TableCell>Client Rates (source)</TableCell><TableCell>Recent Purchase</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {results.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>{r.description}</TableCell>
                    <TableCell>{r.unit ?? '—'}</TableCell>
                    <TableCell align="right">{r.quantity ?? '—'}</TableCell>
                    <TableCell align="right">{r.bestRate == null ? <Chip size="small" variant="outlined" label="not found" /> : <strong>{inr(r.bestRate)}</strong>}</TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        {r.matches.length === 0 ? '—' : r.matches.map((m, j) => (
                          <Tooltip key={j} title={`${m.rateCardName} · ${m.rateCardCode}`}>
                            <span>{inr(m.rate)} <Typography component="span" variant="caption" color="text.secondary">· {m.clientName ?? m.rateCardName} ({m.rateCardCode})</Typography></span>
                          </Tooltip>
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {r.recentPurchase
                        ? <Stack spacing={0}>
                            <span>{inr(r.recentPurchase.rate)} <Typography component="span" variant="caption" color="text.secondary">· {r.recentPurchase.warehouseName ?? '—'}</Typography>{r.recentPurchase.approx && <Chip size="small" color="warning" variant="outlined" label="≈ approx" sx={{ ml: 0.5, height: 18 }} />}</span>
                            <Typography variant="caption" color="text.secondary">{r.recentPurchase.purchaseCode}{r.recentPurchase.date ? ` · ${new Date(r.recentPurchase.date).toLocaleDateString('en-IN')}` : ''}{r.recentPurchase.purchaseCount > 1 ? ` · ${r.recentPurchase.purchaseCount} buys` : ''}</Typography>
                            {r.recentPurchase.approx && <Typography variant="caption" color="text.secondary">matched: {r.recentPurchase.itemName}</Typography>}
                          </Stack>
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </Stack>
    </>
  );
}
